import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Button,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Space,
  Steps,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd';
import type { AllyDocumentType, CreateAllyBody } from '@goelectric/shared-types';
import {
  buildCreateAllyPayload,
  type CreateAllyAdminState,
  type CreateAllyFormState,
} from '../utils/fiscal';
import { ALLY_DUPLICATE_MESSAGES } from '../utils/allyErrors';
import { allyApi } from '../api/allyApi';
import { DOCUMENT_TYPE_LABELS, PERSON_TYPE_LABELS } from '../utils/format';
import { AllyAdminsFields } from './AllyAdminsFields';
import { AllyFiscalFields } from './AllyFiscalFields';

export interface AllyFormProps {
  /** Recibe el cuerpo del contrato ya compuesto; la llamada a la API la hace la página. */
  onSubmit: (body: CreateAllyBody) => void;
  submitting?: boolean;
  /**
   * Instancia del `Form` para operarla desde afuera (p. ej. la página marcando el campo
   * de un error del backend). Si no se pasa, el formulario crea la suya.
   */
  form?: FormInstance<CreateAllyFormState>;
  /**
   * Campo que el backend marcó en un error. Si pertenece a un paso que no es el que el
   * usuario está viendo, el wizard salta a ese paso para que pueda corregirlo.
   */
  errorField?: string | null;
}

const INITIAL_VALUES: Partial<CreateAllyFormState> = {
  personType: 'NATURAL',
  documentType: 'CC',
};

/** Único lugar donde se declara qué campos pertenecen a cada paso (fuente del mapeo campo→paso). */
const STEPS: { title: string; fields: (keyof CreateAllyFormState)[] }[] = [
  {
    title: 'Identificación fiscal',
    fields: [
      'personType',
      'legalName',
      'firstName',
      'lastName',
      'documentType',
      'documentNumber',
      'registeredInRut',
      'checkDigit',
    ],
  },
  { title: 'Contacto y comercial', fields: ['email', 'phone', 'commissionRatePct'] },
  { title: 'Administradores', fields: ['admins', 'ownerIsAdmin'] },
  { title: 'Revisión', fields: [] },
];

const LAST_STEP = STEPS.length - 1;

/** Campo → índice de paso, derivado de `STEPS` (el salto por error y el resumen lo reusan). */
const FIELD_STEP = new Map<string, number>(
  STEPS.flatMap((step, index) => step.fields.map((field) => [field as string, index] as const)),
);

/**
 * Formulario de alta de aliado, en 4 pasos (US-2.2): identificación fiscal → contacto y
 * comercial → administradores (opcional) → revisión.
 *
 * Los campos de todos los pasos están **montados**; los inactivos se ocultan con CSS, así
 * los valores persisten y los efectos entre bloques (el titular espejado como administrador)
 * siguen funcionando sin duplicar estado. "Siguiente" valida solo el paso actual; la
 * revisión reusa `STEPS` para mostrar lo que se va a enviar.
 */
export function AllyForm({ onSubmit, submitting = false, form: formProp, errorField }: AllyFormProps) {
  const [internalForm] = Form.useForm<CreateAllyFormState>();
  const form = formProp ?? internalForm;
  const [current, setCurrent] = useState(0);
  // Mientras corre el chequeo consultivo de unicidad (TG-118): el botón muestra el spinner.
  const [checking, setChecking] = useState(false);
  // Emails tomados detectados al avanzar del paso de administradores; alimenta el validador
  // de cada fila para que el error salga por el Form.Item.
  const [takenEmails, setTakenEmails] = useState<ReadonlySet<string>>(new Set());

  const handleFinish = (values: CreateAllyFormState) => {
    onSubmit(buildCreateAllyPayload(values));
  };

  // Salto declarativo al paso del campo que marcó el backend (sin refs ni handles).
  useEffect(() => {
    if (!errorField) {
      return;
    }
    const step = FIELD_STEP.get(errorField);
    if (step !== undefined) {
      setCurrent(step);
    }
  }, [errorField]);

  /**
   * Chequeo consultivo de unicidad al avanzar de paso (TG-118). Devuelve `true` si hay un
   * duplicado y **no** se debe avanzar (ya marca los campos). Es **fail-open**: si la
   * consulta falla (red, 500, timeout) devuelve `false` y deja avanzar, porque la verdad
   * sigue siendo el constraint de la DB y el POST final; un endpoint de consulta caído no
   * puede bloquear el alta. La carrera entre chequeo y POST existe y no se elimina acá.
   */
  const hasDuplicatesInCurrentStep = async (): Promise<boolean> => {
    // Paso fiscal: unicidad del número de documento.
    if (current === 0) {
      const documentNumber = (form.getFieldValue('documentNumber') as string | undefined)?.trim();
      if (!documentNumber) {
        return false;
      }
      setChecking(true);
      try {
        const { available } = await allyApi.checkDocumentAvailability(documentNumber);
        if (available) {
          return false;
        }
        form.setFields([
          { name: 'documentNumber', errors: [ALLY_DUPLICATE_MESSAGES.documentNumber] },
        ]);
        return true;
      } catch {
        return false;
      } finally {
        setChecking(false);
      }
    }

    // Paso contacto: unicidad del email del aliado (Q5, TG-118).
    if (current === 1) {
      const email = (form.getFieldValue('email') as string | undefined)?.trim();
      if (!email) {
        return false;
      }
      setChecking(true);
      try {
        const { available } = await allyApi.checkEmailAvailability(email);
        if (available) {
          return false;
        }
        form.setFields([{ name: 'email', errors: [ALLY_DUPLICATE_MESSAGES.email] }]);
        return true;
      } catch {
        return false;
      } finally {
        setChecking(false);
      }
    }

    // Paso administradores: los emails van en UNA sola llamada (bulk).
    if (current === 2) {
      const admins = (form.getFieldValue('admins') as CreateAllyAdminState[] | undefined) ?? [];
      const emails = admins
        .map((admin) => admin?.email?.trim() ?? '')
        .filter((email) => email !== '');
      if (emails.length === 0) {
        return false;
      }
      setChecking(true);
      try {
        const { taken } = await allyApi.checkEmailsAvailability(emails);
        const takenSet = new Set(taken.map((email) => email.trim()));
        // Marca las filas vía el validador de la regla del email (al cambiar el set, el
        // bloque re-valida y el Form.Item pinta el error nativo). `setFields` no renderiza
        // errores en campos dentro de un `Form.List`.
        setTakenEmails(takenSet);
        return takenSet.size > 0;
      } catch {
        return false;
      } finally {
        setChecking(false);
      }
    }

    return false;
  };

  /** Avanza solo si el paso valida y no hay duplicados; hacia atrás no se valida nada. */
  const goNext = async () => {
    try {
      await form.validateFields(STEPS[current]?.fields ?? []);
    } catch {
      // El paso no valida: antd ya muestra los errores inline.
      return;
    }
    if (await hasDuplicatesInCurrentStep()) {
      return;
    }
    setCurrent((step) => Math.min(step + 1, LAST_STEP));
  };

  // Submit final: si algún campo de un paso anterior quedó inválido (raro), salta a su paso
  // en vez de fallar en silencio con un botón que "no hace nada".
  const handleFinishFailed = (info: { errorFields: { name: (string | number)[] }[] }) => {
    const first = info.errorFields[0]?.name?.[0];
    const step = typeof first === 'string' ? FIELD_STEP.get(first) : undefined;
    if (step !== undefined) {
      setCurrent(step);
    }
  };

  return (
    <Form
      className="aliados-form"
      form={form}
      layout="vertical"
      initialValues={INITIAL_VALUES}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
    >
      <div className="aliados-form__header">
        {/* El progreso ES este stepper: los conectores entre círculos se llenan hasta el paso
            actual (antd pinta el tail del paso ya superado). `labelPlacement="vertical"` es lo
            que habilita el tail como conector; el nombre sube ENCIMA del círculo por CSS
            (`global.css`), porque antd no tiene un "arriba". Sin porcentaje: nada de `%`. */}
        <Steps
          current={current}
          size="small"
          labelPlacement="vertical"
          items={STEPS.map((step) => ({ title: step.title }))}
          style={{ marginBottom: 12 }}
        />
      </div>

      <div style={{ display: current === 0 ? undefined : 'none' }}>
        <AllyFiscalFields form={form} />
      </div>

      <div style={{ display: current === 1 ? undefined : 'none' }}>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Ingresa el email de contacto.' },
            { type: 'email', message: 'Ingresa un email válido.' },
          ]}
        >
          <Input autoComplete="off" placeholder="Introduzca el email del aliado" />
        </Form.Item>

        <Form.Item label="Teléfono" name="phone">
          <Input autoComplete="off" placeholder="Introduzca el teléfono del aliado" />
        </Form.Item>

        <Form.Item
          label="% de comisión"
          name="commissionRatePct"
          rules={[
            { required: true, message: 'Ingresa el porcentaje de comisión.' },
            { type: 'number', min: 0, max: 100, message: 'El porcentaje va entre 0 y 100.' },
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            precision={2}
            style={{ width: '100%' }}
            placeholder="Introduzca el porcentaje"
          />
        </Form.Item>
      </div>

      <div style={{ display: current === 2 ? undefined : 'none' }}>
        <AllyAdminsFields form={form} takenEmails={takenEmails} />
      </div>

      {current === LAST_STEP && <AllyReview form={form} />}

      <Space style={{ marginTop: 8 }}>
        {current > 0 && <Button onClick={() => setCurrent((step) => step - 1)}>Anterior</Button>}
        {current < LAST_STEP && (
          <Button type="primary" onClick={goNext} loading={checking}>
            Siguiente
          </Button>
        )}
        {current === LAST_STEP && (
          <Button type="primary" htmlType="submit" loading={submitting}>
            Crear aliado
          </Button>
        )}
      </Space>
    </Form>
  );
}

const isJuridica = (values: CreateAllyFormState) => values.personType === 'JURIDICA';
// Tipo efectivo del contrato: la natural inscrita en el RUT viaja como NIT (mismo número).
const effectiveDocumentType = (values: CreateAllyFormState): AllyDocumentType =>
  isJuridica(values) || values.registeredInRut ? 'NIT' : values.documentType;
const isNit = (values: CreateAllyFormState) => effectiveDocumentType(values) === 'NIT';

interface SummaryField {
  label: string;
  /** Campo condicional: solo aparece si aplica al estado actual. */
  when?: (values: CreateAllyFormState) => boolean;
  value: (values: CreateAllyFormState) => ReactNode;
}

/** Presentación de cada campo en el resumen; el orden y la agrupación salen de `STEPS`. */
const SUMMARY_FIELDS: Partial<Record<keyof CreateAllyFormState, SummaryField>> = {
  personType: { label: 'Tipo de persona', value: (v) => PERSON_TYPE_LABELS[v.personType] },
  documentType: {
    label: 'Tipo de documento',
    when: (v) => !isJuridica(v),
    // El resumen muestra lo que el operador eligió (CC/CE), no el tipo efectivo del contrato.
    value: (v) => DOCUMENT_TYPE_LABELS[v.documentType],
  },
  documentNumber: {
    label: 'Número de documento',
    // Pelado: el compuesto con el DV vive en su propia fila derivada ("NIT (cédula + DV)").
    value: (v) => v.documentNumber || '—',
  },
  checkDigit: {
    label: 'Dígito de verificación (en el RUT)',
    when: isNit,
    value: (v) => String(v.checkDigit ?? '—'),
  },
  legalName: { label: 'Razón social', when: isJuridica, value: (v) => v.legalName || '—' },
  firstName: {
    label: 'Nombres',
    when: (v) => !isJuridica(v),
    value: (v) => v.firstName || '—',
  },
  lastName: {
    label: 'Apellidos',
    when: (v) => !isJuridica(v),
    value: (v) => v.lastName || '—',
  },
  email: { label: 'Email', value: (v) => v.email || '—' },
  phone: { label: 'Teléfono', value: (v) => v.phone || '—' },
  commissionRatePct: {
    label: '% de comisión',
    value: (v) => (v.commissionRatePct != null ? `${v.commissionRatePct}%` : '—'),
  },
  ownerIsAdmin: {
    label: 'El titular es el administrador',
    when: (v) => !isJuridica(v),
    value: (v) => (v.ownerIsAdmin ? 'Sí' : 'No'),
  },
  admins: { label: 'Administradores', value: (v) => <AdminsSummary admins={v.admins} /> },
};

/**
 * Filas derivadas del resumen: valores compuestos que **no son campos del form** (por eso no
 * entran en `SUMMARY_FIELDS`) pero deben verse. `after` indica tras qué campo insertarlas para
 * respetar el orden de la pantalla.
 */
interface DerivedSummaryRow extends SummaryField {
  after: keyof CreateAllyFormState;
}

const DERIVED_SUMMARY_ROWS: DerivedSummaryRow[] = [
  {
    after: 'checkDigit',
    label: 'NIT (cédula + DV)',
    when: (v) => isNit(v) && !isJuridica(v),
    value: (v) => `${v.documentNumber}-${v.checkDigit}`,
  },
  {
    after: 'checkDigit',
    label: 'NIT (número + DV)',
    when: isJuridica,
    value: (v) => `${v.documentNumber}-${v.checkDigit}`,
  },
];

/** Administradores que efectivamente viajan en el contrato (nombre + email completos). */
function AdminsSummary({ admins }: { admins?: CreateAllyAdminState[] }) {
  const complete = (admins ?? []).filter((admin) => admin.name?.trim() && admin.email?.trim());
  if (complete.length === 0) {
    return <Typography.Text type="secondary">Sin administradores</Typography.Text>;
  }
  return (
    <Space direction="vertical" size={0}>
      {complete.map((admin, index) => (
        <span key={index}>
          <Typography.Text strong>{admin.name}</Typography.Text>{' '}
          <Typography.Text type="secondary">{admin.email}</Typography.Text>
        </span>
      ))}
    </Space>
  );
}

/**
 * Paso 4: resumen de lo que se va a enviar. Se monta solo en la revisión (no tiene campos),
 * así no re-renderiza el wizard en cada tecla. No muestra links de invitación: nacen en la
 * respuesta del POST.
 */
function AllyReview({ form }: { form: FormInstance<CreateAllyFormState> }) {
  const values = Form.useWatch([], form) ?? ({} as CreateAllyFormState);

  return (
    <>
      {STEPS.slice(0, LAST_STEP).map((step) => {
        const items = step.fields.flatMap((field) => {
          const rows: { key: string; meta: SummaryField }[] = [];
          const meta = SUMMARY_FIELDS[field];
          if (meta && (!meta.when || meta.when(values))) {
            rows.push({ key: field, meta });
          }
          for (const derived of DERIVED_SUMMARY_ROWS) {
            if (derived.after === field && (!derived.when || derived.when(values))) {
              rows.push({ key: derived.label, meta: derived });
            }
          }
          return rows;
        });
        if (items.length === 0) {
          return null;
        }
        return (
          <Descriptions
            key={step.title}
            title={step.title}
            column={1}
            size="small"
            bordered
            style={{ marginBottom: 16 }}
          >
            {items.map(({ key, meta }) => (
              <Descriptions.Item key={key} label={meta.label}>
                {meta.value(values)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        );
      })}
    </>
  );
}
